import { onlyDigits } from '@/lib/documentUtils';

/**
 * Endereço do cliente: os campos ESTRUTURADOS (para a nota fiscal) e a linha
 * ÚNICA legada (`clients.address` / `quotes.client_address`), que é o que o PDF
 * imprime e o que todo documento antigo tem.
 *
 * As duas formas convivem de propósito: a estruturada é a que a NFS-e precisa, e
 * a linha única é a que já está gravada em todo o histórico. Compor a linha a
 * partir dos campos — em vez de escolher uma das duas — mantém o PDF idêntico ao
 * de sempre para quem preenche o endereço detalhado, e não obriga ninguém a
 * preencher CEP para mandar um orçamento.
 */
export interface ClientAddressFields {
  logradouro?: string;
  numero?: string;
  bairro?: string;
  municipioNome?: string;
  uf?: string;
}

/** "Rua X, 123 - Centro - São Paulo/SP" a partir dos campos estruturados. */
export function composeAddressLine(f: ClientAddressFields): string {
  const line1 = [f.logradouro?.trim(), f.numero?.trim()].filter(Boolean).join(', ');
  const cityUf = [f.municipioNome?.trim(), f.uf?.trim()].filter(Boolean).join('/');
  return [line1, f.bairro?.trim(), cityUf].filter(Boolean).join(' - ');
}

/** Os campos de cliente que o formulário de orçamento coleta. */
export interface QuoteClientFields {
  clientName: string;
  newClientPhone?: string;
  newClientAddress?: string;
  newClientDocument?: string;
  newClientEmail?: string;
  newClientCep?: string;
  newClientLogradouro?: string;
  newClientNumero?: string;
  newClientComplemento?: string;
  newClientBairro?: string;
  newClientMunicipio?: string;
  newClientUf?: string;
  newClientIe?: string;
}

/**
 * Endereço em linha única que vai para o DOCUMENTO (`quotes.client_address`).
 *
 * Precedência: o que o usuário digitou na linha livre vence; senão, a linha é
 * composta dos campos estruturados. Sem esta regra, quem preenche só CEP+número
 * teria o endereço no cadastro do cliente e um documento SEM endereço nenhum.
 */
export function resolveClientAddressLine(data: QuoteClientFields): string | null {
  const livre = data.newClientAddress?.trim();
  if (livre) return livre;
  const composto = composeAddressLine({
    logradouro: data.newClientLogradouro,
    numero: data.newClientNumero,
    bairro: data.newClientBairro,
    municipioNome: data.newClientMunicipio,
    uf: data.newClientUf,
  });
  return composto || null;
}

/**
 * Colunas de `clients` para o cliente criado a partir do orçamento.
 *
 * Builder ÚNICO de propósito: são três caminhos que inserem este mesmo cliente
 * (os dois saves online da tela de orçamento e a fila offline). Montar a lista de
 * colunas em cada um é a armadilha #33/#62 — a coluna nova entra em dois e
 * "some" no terceiro, com toast de sucesso e sem erro nenhum.
 */
export function buildNewClientColumns(data: QuoteClientFields) {
  return {
    name: data.clientName.trim(),
    phone: data.newClientPhone?.trim() || null,
    address: resolveClientAddressLine(data),
    document: onlyDigits(data.newClientDocument || '') || null,
    email: data.newClientEmail?.trim() || null,
    cep: onlyDigits(data.newClientCep || '') || null,
    logradouro: data.newClientLogradouro?.trim() || null,
    numero: data.newClientNumero?.trim() || null,
    complemento: data.newClientComplemento?.trim() || null,
    bairro: data.newClientBairro?.trim() || null,
    municipio_nome: data.newClientMunicipio?.trim() || null,
    uf: data.newClientUf?.trim() || null,
    inscricao_estadual: data.newClientIe?.trim() || null,
  };
}
